import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import EmailMenu from "components/email-menu";
import FaxMenu from "components/fax-menu";
import SupaDemoButton from "components/supa-demo-button";
import {
  COMPACT_TOP_BAR,
  FAX_FEATURE_FLAG,
  SCHEDULING_V3_FEATURE,
  VERTICAL_SOUTHWESTERN_LIMITATIONS,
  VISUALIZATION_MODAL
} from "constants/feature-flags";
import AiVisualizationGenerator from "features/ai-visualization-generator";
import { useSideMenuChoice } from "features/app-layout/side-menu-choice/use-side-menu-choice.hook";
import { selectCurrentUser } from "features/auth/auth-slice";
import { usePatientDrawer } from "features/patients/patient-drawer";
import TasksButton from "features/patients/patient-header/header-tasks/tasks-button.component";
import PatientSearchWithQuickAdd from "features/patients/patient-search-with-quick-add";
import useIsMobile from "hooks/use-is-mobile.hook";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import { MENU } from "../layout-side-menu";
import AccountMenu from "./account-menu";
import CurrentClinicRegionSelector from "./clinic-region-selector/clinic-regions-selector.component";
import MaintenanceBanner from "./maintenance-banner.component";
import NotificationsMenu from "./notifications-menu";
import ReferralsButton from "./referrals/referrals-button.component";
import StaleBuildPill from "./stale-build-pill/stale-build-pill.component";
import TopBarMoreMenu from "./top-bar-more-menu.component";

export default function LayoutTopBar() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const flags = useFlags();
  const params = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen } = usePatientDrawer();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));
  /**
   * Below 1,760 px the bar did not fit: measured on a real practice it scrolled the page 95 px
   * at 1024, 54 at 1280 and 28 at 1440, pushed the account menu off-screen and laid Privacy
   * Mode over the search and New Patient. Its two widest labelled controls become icons
   * with tooltips there (ENG-2009). The mobile menu keeps its labels; it has the room.
   */
  // Measured: the labelled bar needs ~1,710 px on a real practice (it still overflowed or
  // overlapped at 1,536, 1,600 and 1,700), so it compacts below 1,760 rather than below the
  // theme's `xl`. The account name is the variable part; a longer one needs more.
  const hasRoomForLabels = useMediaQuery("(min-width:1760px)");
  // Behind `compact-top-bar` AND Scheduling V3 (ENG-2038). The fitting bar is app-wide — it
  // changes every page — so while V3 is piloted it reaches only the people already in that
  // pilot, and nobody else's top bar changes shape. It is an audience gate, not a route gate:
  // a bar that reshaped between /scheduling-v3 and the rest of the app would be worse than
  // either consistent state. Retire both this gate and the flag when V3 is general.
  // LaunchDarkly prerequisites would express this without code, but they are not available
  // on this plan (`invalid_patch: Plan does not allow this operation`, 2026-09-20).
  const isCompactTopBarOn =
    Boolean(flags[COMPACT_TOP_BAR]) && Boolean(flags[SCHEDULING_V3_FEATURE]);
  const compact = isCompactTopBarOn && !hasRoomForLabels && !isMobile;
  /**
   * Even with those icons the bar did not fit: below 1,760 px the less-used controls
   * (referrals, AI visualisation, fax, email) move into a "More" menu there. Below `lg` the
   * page title steps down to 24 px. Privacy Mode, the region, tasks, notifications and the
   * account always stay on the bar. (Administration moved to the side menu's footer, ENG-2535.)
   */
  const withMoreMenu = compact;
  const { isPrivateMode, handlePrivacyModeChange } = usePatientPrivacy();
  // Financial Reports Only Access: the top bar keeps its chrome (clinic selector, account menu)
  // but drops every patient-facing control — global patient search above all, since it exposes
  // PHI the restriction exists to hide.
  const { isAdminOrBusinessOwner, isReportsOnlyUser } = useUserRoles();
  const currentUser = useAppSelector(selectCurrentUser);
  // ENG-2535: the redesigned side menu carries Administration in its footer; the classic
  // rail does not, so the top bar keeps offering it while that one is showing.
  const { useNew: newSideMenu } = useSideMenuChoice();
  const adminInTopBar =
    !newSideMenu && Boolean(currentUser?.has_access_to_admin_panel);
  const adminInMoreMenu = compact && isBelowLg;
  const navigateToAdministrationPage = useCallback(
    () => navigate("/administration"),
    [navigate]
  );

  const patientId = String(params["*"] ?? "").split("/")[0];

  const currentPage =
    MENU.find(({ to }) => pathname.startsWith(to)) ||
    (pathname.startsWith("/home")
      ? { primary: "Home", supaDemoId: undefined }
      : undefined) ||
    MENU.find(({ isFallbackPath }) => isFallbackPath);

  const isFaxFeatureEnabled = flags[FAX_FEATURE_FLAG];
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const isVisualizationModalEnabled = flags[VISUALIZATION_MODAL];

  const [mobileAnchorEl, setMobileAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const handleMobileMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMobileAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null);
  };

  const openMobileMenu = Boolean(mobileAnchorEl);
  const mobileId = openMobileMenu ? "mobile-popover" : undefined;

  const createNewPatientButton = useMemo(() => {
    if (compact)
      return (
        <Tooltip title="New Patient">
          <Button
            data-testid="new-patient-button"
            variant="outlined"
            size="small"
            aria-label="New Patient"
            sx={{ minWidth: 0, width: 40, height: 32, borderRadius: 2, p: 0 }}
            onClick={() => navigate("/patients/new")}
          >
            <PersonAddAltOutlinedIcon fontSize="small" />
          </Button>
        </Tooltip>
      );
    return (
      <Button
        data-testid="new-patient-button"
        variant="outlined"
        size="small"
        sx={{
          minWidth: "108px",
          width: "245px",
          borderRadius: 2,
          height: 32,
          textTransform: "none",
          fontSize: 14,
          p: 0
        }}
        onClick={() => navigate("/patients/new")}
      >
        <AddIcon fontSize="small" />
        New&nbsp;Patient
      </Button>
    );
  }, [compact, navigate]);

  const privacyToggle = isReportsOnlyUser ? null : (
    <>
      {compact ? (
        <Tooltip title="Privacy Mode">
          <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
            <VisibilityOffOutlinedIcon
              aria-hidden
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
            <Switch
              checked={isPrivateMode}
              onChange={handlePrivacyModeChange}
              inputProps={{ "aria-label": "Privacy Mode" }}
            />
          </Stack>
        </Tooltip>
      ) : (
        <FormControlLabel
          control={
            <Switch
              checked={isPrivateMode}
              onChange={handlePrivacyModeChange}
            />
          }
          label="Privacy Mode"
          // MUI's default -11 px pulls the label over the button before it: it overlapped
          // the referrals button at every width, 3 px even at 1920.
          sx={isCompactTopBarOn ? { ml: 0, flexShrink: 0 } : undefined}
        />
      )}
    </>
  );

  const menuItemsPart1 = isReportsOnlyUser ? null : (
    <>
      <ReferralsButton />

      {privacyToggle}

      {isVisualizationModalEnabled && <AiVisualizationGenerator />}
    </>
  );

  const faxAndEmail = (
    <>
      {!isReportsOnlyUser &&
        isFaxFeatureEnabled &&
        !(
          verticalSouthwesternLimitationsEnabled && !isAdminOrBusinessOwner
        ) && <FaxMenu />}
      {!isReportsOnlyUser && <EmailMenu patientId={Number(patientId)} />}
    </>
  );

  const menuItemsPart2 = (
    <>
      <CurrentClinicRegionSelector isLargeScreen={isLargeScreen} />

      {faxAndEmail}
    </>
  );

  return (
    <Box
      id="layout-top-bar"
      data-testid="layout-top-bar"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        gap: 2,
        boxSizing: "border-box",
        border: `1px solid ${theme.palette.divider}`,
        borderRight: "none",
        pl: 2,
        pr: 1
      }}
    >
      <Stack rowGap={0.5}>
        <Typography
          variant="h1"
          fontWeight="fontWeightBold"
          color="textPrimary"
          data-testid="page-title"
          sx={{
            fontSize: isMobile
              ? "20px"
              : isCompactTopBarOn && isBelowLg
                ? "24px"
                : "32px",
            lineHeight: 1.1,
            fontWeight: isMobile ? 800 : undefined,
            pl: isMobile ? 4.5 : undefined
          }}
        >
          {currentPage?.primary}
        </Typography>
        {currentPage?.supaDemoId && (
          <SupaDemoButton supaDemoId={currentPage?.supaDemoId} />
        )}
      </Stack>

      {!isMobile && !adminInMoreMenu && adminInTopBar && (
        <Button
          variant="outlined"
          size="small"
          onClick={navigateToAdministrationPage}
        >
          Administration
        </Button>
      )}

      <StaleBuildPill />
      <MaintenanceBanner />
      <Stack
        width="100%"
        display="flex"
        alignItems={isMobile ? "end" : "center"}
        direction={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "flex-end" : "space-between"}
        gap={isLargeScreen ? 2 : 1}
        maxWidth="84%"
      >
        {/*
          ⚠️ `minWidth: 0` is what stops this bar pushing the whole DOCUMENT sideways.

          A flex item defaults to `min-width: auto`, i.e. its own min-content, so this row
          could not shrink below the sum of its children and the overflow escaped all the way
          out to `documentElement`. Measured on a live page at a 1440 viewport: 1508px of
          scrollWidth against 1440 of clientWidth — a horizontal scrollbar on EVERY route
          (/scheduling was worse, at 105px), attributed to whichever page the reader happened
          to be on.
        */}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={isLargeScreen ? 2 : 1}
          sx={{
            width: "100%",
            minWidth: 0
          }}
        >
          {!isMobile && !isReportsOnlyUser && (
            <PatientSearchWithQuickAdd
              showRecent
              showLengthLimitHint
              showSearchModeToggle
              respondToGlobalFocusRequest
              /*
                `PatientSearch` turns this into a hard `minWidth` floor (its line ~292), so at
                560 it was the single largest thing this row could not give back. The bar has
                roughly `0.84 × (viewport − side menu)` to work with, and the right-hand cluster
                is user-dependent — it carries the account name — so the width at which 560
                stops fitting varies per user, which is why this was not universally noticed.

                Unchanged above `xl`, which is where the 560 was chosen for.
              */
              minWidth={isLargeScreen ? 560 : 360}
            />
          )}

          {!isMobile && !isReportsOnlyUser && createNewPatientButton}

          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={isLargeScreen ? 1 : 0}
            sx={{
              width: "100%",
              // Same reason as the Box above: without it this cluster's min-content (the
              // account name is the variable part) is a floor the row cannot shrink past.
              minWidth: 0,
              boxSizing: "border-box",
              "& label": { mr: 0 },
              mr: isMobile ? -2 : 0
            }}
          >
            {isMobile ? (
              <Button
                size="small"
                sx={{
                  background: misc.sunoGreen,
                  color: "white",
                  width: "28px",
                  minWidth: "28px",
                  height: "28px",
                  mr: 1
                }}
                style={{
                  background: misc.sunoGreen,
                  color: "white"
                }}
                onClick={handleMobileMenuClick}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    transform: openMobileMenu
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s ease-in-out"
                  }}
                />
              </Button>
            ) : null}
            <Popover
              id={mobileId}
              open={openMobileMenu}
              anchorEl={mobileAnchorEl}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left"
              }}
              sx={{ mt: 1 }}
            >
              <Stack
                display="flex"
                alignItems="flex-start"
                width="100%"
                pt={2}
                pb={1}
                pl={3}
                pr={2}
                sx={{
                  background: grey[50],
                  border: `solid 1px ${grey[300]}`
                }}
              >
                {!isReportsOnlyUser && (
                  <PatientSearchWithQuickAdd
                    showRecent
                    showLengthLimitHint
                    minWidth={280}
                    sx={{
                      width: "280px",
                      height: "32px",
                      mb: 2
                    }}
                  />
                )}
                {adminInTopBar && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={navigateToAdministrationPage}
                    sx={{ mb: 2 }}
                  >
                    Administration
                  </Button>
                )}
                {!isReportsOnlyUser && createNewPatientButton}
                {menuItemsPart1}
                <CurrentClinicRegionSelector isLargeScreen={isLargeScreen} />

                {!isReportsOnlyUser &&
                  isFaxFeatureEnabled &&
                  !(
                    verticalSouthwesternLimitationsEnabled &&
                    !isAdminOrBusinessOwner
                  ) && <FaxMenu />}
                {!isReportsOnlyUser && (
                  <EmailMenu patientId={Number(patientId)} />
                )}
              </Stack>
            </Popover>

            {!isMobile && (withMoreMenu ? privacyToggle : menuItemsPart1)}

            {!isOpen && !isReportsOnlyUser && (
              <Box sx={{ px: isMobile ? 0 : 1 }}>
                <TasksButton />
              </Box>
            )}

            {!isMobile && !withMoreMenu && menuItemsPart2}
            {withMoreMenu && (
              <>
                <CurrentClinicRegionSelector isLargeScreen={isLargeScreen} />
                {!isReportsOnlyUser && (
                  <TopBarMoreMenu>
                    {adminInMoreMenu && adminInTopBar && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={navigateToAdministrationPage}
                      >
                        Administration
                      </Button>
                    )}
                    <ReferralsButton />
                    {isVisualizationModalEnabled && (
                      <AiVisualizationGenerator />
                    )}
                    {faxAndEmail}
                  </TopBarMoreMenu>
                )}
              </>
            )}

            {!isReportsOnlyUser && <NotificationsMenu />}
            <AccountMenu />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
